import { permissionManifest } from "@msuite/citadel";
import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type UserPermissionFormSchema, userPermissionFormSchema } from "@msuite/utils/schemas";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";
import { useFormAutosave } from "@/hooks/use-form-autosave";
import { useUserPermissionsForm } from "@/hooks/use-user-permissions-form";
import type { UserPanelBaseProps } from "../../types";
import {
	customUserGrantKeys,
	roleUnions,
	toPermissionFormValues,
	toRoleSources,
	toSubmitPermissions,
	wildcardRoleNames,
} from "./permission-form-values";
import { createColumns } from "./table-config";

export function UserPermissions({ userId }: UserPanelBaseProps) {
	/** Context */
	const { t, language } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();

	/** Query */
	// The same key the roles table invalidates after a role toggle — one shared hook, so the
	// two readers cannot drift apart on it. A query rather than async `defaultValues`: that
	// runs once and never again, and this grid must re-seed whenever a role toggle next door
	// changes what the cells pre-select.
	const { form, isLoading } = useUserPermissionsForm(userId);

	/** Form */
	const values = useMemo(() => (form ? toPermissionFormValues(form) : undefined), [form]);
	const methods = useForm<UserPermissionFormSchema>({
		resolver: resolver(userPermissionFormSchema),
		defaultValues: { permissions: {} },
		// `values` re-seeds the form whenever the query answers again, which is what carries a
		// role toggle next door into the cells' pre-selections.
		values,
		// A reseed that lands while an edit's save is still in flight keeps the edit — the same
		// arrangement as the role editor, explained there.
		resetOptions: { keepDirtyValues: true },
	});

	/** Functions */
	async function handleOnSubmit(json: UserPermissionFormSchema) {
		try {
			const response = await client.users[":userId"].permissions.$patch({
				param: { userId },
				// Custom pairs are stripped — their cells are read-only, and a replace-write
				// carrying them as `false` would revoke rows the form never let anyone touch —
				// and role-contained values normalise to `false`, so a save never plants a row
				// that grants nothing.
				json: { ...json, permissions: toSubmitPermissions(json.permissions, unions, customKeys) },
			});
			await responseHandler(response, false);
			// One message per outcome — the same shape as the role editor for the same act, so
			// saving permissions says so on both screens. The query cache is persisted to
			// localStorage, so it has to be dropped — otherwise the next mount re-seeds the grid
			// from the set that was just replaced.
			toast.success(t("permissions.successfully_updated"));
			await qc.invalidateQueries({ predicate: invalidate(["user-permissions-form", "citadel"]) });
			// The burst's last save settles the form and clears the dirty flags the reseed above
			// preserved — otherwise those cells would sit out every future reseed.
			if (!autosave.hasQueuedSave()) methods.reset(json);
		} catch (error) {
			// A cell showing a reach the server refused would be a lie, and with saves happening
			// on selection there is no pending state to hold for a retry — the grid returns to
			// the last seeded truth, and the next selection is the retry.
			toast.error(error instanceof Error ? error.message : t("permissions.failed_to_update"));
			if (values) methods.reset(values);
		}
	}

	const onSubmit = methods.handleSubmit(handleOnSubmit);
	const autosave = useFormAutosave(methods, onSubmit);

	/** Computed */
	const roleSources = useMemo(() => (form ? toRoleSources(form) : {}), [form]);
	const unions = useMemo(() => (form ? roleUnions(form) : {}), [form]);
	const customKeys = useMemo(() => (form ? customUserGrantKeys(form) : new Set<string>()), [form]);
	const wildcardRoles = useMemo(() => (form ? wildcardRoleNames(form) : []), [form]);
	const columns = useMemo(
		() => createColumns({ roleSources, customKeys }),
		[roleSources, customKeys, language],
	);
	const data = useMemo(
		() => permissionManifest.map((entry) => ({ ...entry, id: entry.subject })),
		[],
	);

	/** Render */
	// An owner's access never touches a permission row — the resolver answers for them before
	// it reads one — so a grid of editable cells would be a lie with working controls, the same
	// judgement the roles tab makes. The wildcard case is the admin role's `manage all`: no
	// manifest pair carries the subject `all`, so cell by cell the grid would claim "no access"
	// for exactly the users who hold everything.
	if (form?.isOwner) {
		return (
			<ContentCard
				title={t("permissions.permissions")}
				icon={icon("Lock")}
			>
				<p className="label">{t("permissions.owner_permissions_note")}</p>
			</ContentCard>
		);
	}

	if (wildcardRoles.length > 0) {
		return (
			<ContentCard
				title={t("permissions.permissions")}
				icon={icon("Lock")}
			>
				<p className="label">
					{t("permissions.wildcard_permissions_note", { roles: wildcardRoles.join(", ") })}
				</p>
			</ContentCard>
		);
	}

	// No sizing wrapper: the grid fills the tab panel exactly like the role editor fills its
	// own — an extra wrapper inside the block-level tab panel resolved to zero height, and the
	// virtualised rows rendered into an invisible, clipped box.
	return (
		<FormProvider {...methods}>
			<DataTable
				persistenceKey="user-permissions-table"
				data={data}
				// No refresh control: the rows are a client-side constant and the values live in
				// the form, so there is nothing to refetch — and a refetch mid-edit would discard
				// the edits. A no-op instead rendered the button, whose spinner asks
				// `isFetching({ queryKey })`; with an empty key that is a prefix matching every
				// query in the app, so it span whenever anything anywhere loaded.
				refetch={undefined}
				initialPinningState={{ start: ["subject"] }}
				queryKey={undefined}
				menu={undefined}
				isLoading={isLoading}
				columns={columns}
			/>
		</FormProvider>
	);
}
